import { useEffect, useRef, useState } from 'react';
import { AppBridge, PostMessageTransport } from '@modelcontextprotocol/ext-apps/app-bridge';
import { ReadMcpResourceService } from '../../services/chat';
import {
  createAppBridge,
  getWidgetErrorMessage,
  toBridgeToolResult,
} from '../../lib/mcp-app-bridge';
import { extractResourceHtml } from '../../lib/mcp-tool-utils';
import {
  DEFAULT_SANDBOX_ATTRIBUTE,
  SANDBOX_PROXY_READY_NOTIFICATION,
} from '../../lib/mcp-sandbox.constants';

type McpWidgetContainerProps = {
  mcpId: number;
  resourceUri: string;
  toolName: string;
  toolInput?: unknown;
  toolResult?: unknown;
  onError?: (message: string) => void;
};

const INIT_TIMEOUT_MS = 60000;

const resourceHtmlCache = new Map<string, string>();
const resourceHtmlInflight = new Map<string, Promise<string>>();

function resourceCacheKey(mcpId: number, resourceUri: string) {
  return `${mcpId}:${resourceUri}`;
}

/** Drop cached widget HTML after MCP reconnect, refresh, or tool list changes. */
export function invalidateMcpWidgetHtmlCache(mcpId?: number, resourceUri?: string): void {
  if (mcpId == null) {
    resourceHtmlCache.clear();
    resourceHtmlInflight.clear();
    return;
  }

  if (resourceUri) {
    const loadKey = resourceCacheKey(mcpId, resourceUri);
    resourceHtmlCache.delete(loadKey);
    resourceHtmlInflight.delete(loadKey);
    return;
  }

  const prefix = `${mcpId}:`;
  Array.from(resourceHtmlCache.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => {
      resourceHtmlCache.delete(key);
    });
  Array.from(resourceHtmlInflight.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => {
      resourceHtmlInflight.delete(key);
    });
}

async function loadWidgetHtml(mcpId: number, resourceUri: string): Promise<string> {
  const loadKey = resourceCacheKey(mcpId, resourceUri);
  const cached = resourceHtmlCache.get(loadKey);
  if (cached) {
    return cached;
  }

  const inflight = resourceHtmlInflight.get(loadKey);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    try {
      const response = await ReadMcpResourceService(mcpId, resourceUri);
      const contents = response?.data?.data?.contents || response?.data?.contents || [];
      const html = extractResourceHtml(contents);
      if (!html) {
        throw new Error('Widget HTML was empty');
      }
      resourceHtmlCache.set(loadKey, html);
      return html;
    } catch (error) {
      resourceHtmlCache.delete(loadKey);
      throw error;
    }
  })();

  resourceHtmlInflight.set(loadKey, promise);

  try {
    return await promise;
  } finally {
    if (resourceHtmlInflight.get(loadKey) === promise) {
      resourceHtmlInflight.delete(loadKey);
    }
  }
}

function waitForSandboxProxyReady(iframe: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const listener = (event: MessageEvent) => {
      if (
        event.source === iframe.contentWindow &&
        event.origin === window.location.origin &&
        event.data?.method === SANDBOX_PROXY_READY_NOTIFICATION
      ) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', listener);
        resolve();
      }
    };

    timeoutId = setTimeout(() => {
      window.removeEventListener('message', listener);
      reject(new Error('Sandbox proxy did not become ready'));
    }, INIT_TIMEOUT_MS);

    window.addEventListener('message', listener);
  });
}

function waitForAppInitialized(appBridge: AppBridge): Promise<void> {
  return new Promise((resolve) => {
    const handler = () => {
      appBridge.removeEventListener('initialized', handler);
      resolve();
    };
    appBridge.addEventListener('initialized', handler);
  });
}

function McpWidgetContainer({
  mcpId,
  resourceUri,
  toolName,
  toolInput,
  toolResult,
  onError,
}: McpWidgetContainerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<AppBridge | null>(null);
  const initializedRef = useRef(false);
  const toolInputSentRef = useRef(false);
  const toolResultSentRef = useRef(false);
  const toolInputRef = useRef(toolInput);
  const toolResultRef = useRef(toolResult);
  const onErrorRef = useRef(onError);

  toolInputRef.current = toolInput;
  toolResultRef.current = toolResult;
  onErrorRef.current = onError;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState(320);

  useEffect(() => {
    let cancelled = false;
    let bridge: AppBridge | null = null;
    let bridgeCleanup: (() => void) | null = null;

    const initializeWidget = async () => {
      setLoading(true);
      setError(null);
      initializedRef.current = false;
      toolInputSentRef.current = false;
      toolResultSentRef.current = false;

      const iframe = iframeRef.current;
      if (!iframe) {
        return;
      }

      try {
        const html = await loadWidgetHtml(mcpId, resourceUri);

        if (cancelled) {
          return;
        }

        const readyPromise = waitForSandboxProxyReady(iframe);
        iframe.src = `${window.location.origin}/sandbox_proxy.html`;
        await readyPromise;

        if (cancelled) {
          return;
        }

        const { bridge: appBridge, cleanup } = createAppBridge(
          mcpId,
          iframe,
          (height) => {
            setIframeHeight(Math.max(120, Math.min(height, 800)));
          },
          { uri: resourceUri, html },
        );
        bridge = appBridge;
        bridgeCleanup = cleanup;
        bridgeRef.current = bridge;

        const initializedPromise = waitForAppInitialized(bridge);
        await bridge.connect(
          new PostMessageTransport(iframe.contentWindow!, iframe.contentWindow!),
        );
        await bridge.sendSandboxResourceReady({ html });
        await initializedPromise;

        if (cancelled) {
          return;
        }

        initializedRef.current = true;
        setLoading(false);

        if (toolInputRef.current !== undefined) {
          await bridge.sendToolInput({
            arguments: (toolInputRef.current as Record<string, unknown>) ?? {},
          });
          toolInputSentRef.current = true;
        }

        if (toolResultRef.current !== undefined) {
          await bridge.sendToolResult(toBridgeToolResult(toolResultRef.current));
          toolResultSentRef.current = true;
        }
      } catch (err) {
        const message = getWidgetErrorMessage(err);
        if (!cancelled && message) {
          setError(message);
          setLoading(false);
          onErrorRef.current?.(message);
        } else if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initializeWidget();

    return () => {
      cancelled = true;
      const activeBridge = bridge ?? bridgeRef.current;
      if (activeBridge) {
        activeBridge.teardownResource({}).catch(() => undefined);
      }
      bridgeCleanup?.();
      bridgeRef.current = null;
      initializedRef.current = false;
    };
  }, [mcpId, resourceUri]);

  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!bridge || !initializedRef.current || toolInput === undefined || toolInputSentRef.current) {
      return;
    }
    bridge
      .sendToolInput({ arguments: (toolInput as Record<string, unknown>) ?? {} })
      .then(() => {
        toolInputSentRef.current = true;
      })
      .catch(() => undefined);
  }, [toolInput]);

  useEffect(() => {
    const bridge = bridgeRef.current;
    if (
      !bridge ||
      !initializedRef.current ||
      toolResult === undefined ||
      toolResultSentRef.current
    ) {
      return;
    }
    bridge
      .sendToolResult(toBridgeToolResult(toolResult))
      .then(() => {
        toolResultSentRef.current = true;
      })
      .catch(() => undefined);
  }, [toolResult]);

  if (error) {
    return (
      <div className="text-sm text-red-600 border border-red-100 rounded p-3 bg-red-50">
        Widget error: {error}
      </div>
    );
  }

  return (
    <div className="mcp-widget-container relative w-full border rounded-lg overflow-hidden bg-white">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 bg-white/80 z-10">
          Loading widget…
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={`MCP widget: ${toolName}`}
        className="w-full border-0"
        style={{ height: iframeHeight }}
        sandbox={DEFAULT_SANDBOX_ATTRIBUTE}
      />
    </div>
  );
}

McpWidgetContainer.defaultProps = {
  toolInput: undefined,
  toolResult: undefined,
  onError: undefined,
};

export default McpWidgetContainer;
